import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowRight, ArrowLeft, Check, Eye, EyeOff, Shield } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  // Fluxo de código (company_admin): o `code_token` chega pelo LINK DO EMAIL.
  // Ele saiu da resposta de /auth/forgot-password — que agora é idêntica para
  // e-mail cadastrado e não cadastrado, para não enumerar identidade — e, de
  // quebra, deixou de ser entregue a quem só sabe o endereço de alguém.
  const codeTokenFromUrl = searchParams.get("code_token");
  const showKyc = searchParams.get("kyc") === "true";
  const navigate = useNavigate();

  // Step states
  const [step, setStep] = useState(
    token ? "new_password" : codeTokenFromUrl ? "code" : showKyc ? "kyc_info" : "email");
  const [loading, setLoading] = useState(false);

  // Email form
  const [email, setEmail] = useState("");

  // Code verification (company flow)
  const [codeToken, setCodeToken] = useState(codeTokenFromUrl || "");
  const [code, setCode] = useState("");
  const [resetToken, setResetToken] = useState(token || "");

  // New password
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const handleForgot = async (e) => {
    e.preventDefault();
    if (!email.trim()) { toast.error("Digite seu email"); return; }
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      // Sempre a mesma tela: a resposta do backend é idêntica para e-mail
      // cadastrado e não cadastrado (nada de `flow`/`code_token` no corpo), e
      // ramificar aqui reabriria a enumeração no cliente. Quem tem conta
      // continua daí pelo link do e-mail.
      setStep("email_sent");
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao enviar"); }
    setLoading(false);
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    if (!code || code.length !== 6) { toast.error("Digite o código de 6 dígitos"); return; }
    setLoading(true);
    try {
      const res = await api.post("/auth/verify-reset-code", { code_token: codeToken, code });
      setResetToken(res.data.reset_token);
      setStep("new_password");
      toast.success("Código verificado!");
    } catch (err) { toast.error(err.response?.data?.detail || "Código inválido"); }
    setLoading(false);
  };

  const handleReset = async (e) => {
    e.preventDefault();
    if (!password || password.length < 6) { toast.error("Senha mínimo 6 caracteres"); return; }
    if (password !== confirmPassword) { toast.error("Senhas não coincidem"); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token: resetToken, password });
      setStep("success");
      toast.success("Senha redefinida com sucesso!");
    } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-background" data-testid="reset-password-page">
      <div className="w-full max-w-md space-y-6 animate-fade-in">
        <div className="flex items-center justify-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center text-white font-bold text-lg">K</div>
          <span className="text-2xl font-bold">Kredz</span>
        </div>

        {step === "email" && (
          <>
            <div className="text-center">
              <h2 className="text-2xl font-bold">Esqueceu a Senha?</h2>
              <p className="text-muted-foreground mt-2">Digite seu email para receber instruções</p>
            </div>
            <form onSubmit={handleForgot} className="space-y-5">
              <div className="space-y-2">
                <Label>Email</Label>
                <Input data-testid="forgot-email-input" type="email" value={email}
                  onChange={(e) => setEmail(e.target.value)} placeholder="seu@email.com"
                  className="h-11 bg-black/20 border-white/10" />
              </div>
              <Button data-testid="forgot-submit-btn" type="submit" disabled={loading}
                className="w-full h-11 bg-blue-600 hover:bg-blue-700 text-white font-semibold">
                {loading ? <div className="loading-spinner" /> : <>Enviar <ArrowRight className="w-4 h-4" /></>}
              </Button>
            </form>
            <div className="text-center space-y-2">
              <Link to="/login" className="text-sm text-muted-foreground hover:text-foreground block">
                <ArrowLeft className="w-3 h-3 inline mr-1" /> Voltar ao Login
              </Link>
              <button onClick={() => setStep("kyc_info")} className="text-sm text-blue-400 hover:text-blue-300">
                Não tenho acesso ao email
              </button>
            </div>
          </>
        )}

        {step === "email_sent" && (
          <div className="text-center space-y-4">
            <div className="w-16 h-16 rounded-full bg-blue-500/10 flex items-center justify-center mx-auto">
              <Check className="w-8 h-8 text-blue-400" />
            </div>
            <h2 className="text-2xl font-bold">Email Enviado!</h2>
            <p className="text-muted-foreground">Se o email estiver cadastrado, você receberá as instruções para redefinir sua senha. Verifique sua caixa de entrada e spam.</p>
            <p className="text-xs text-muted-foreground">As instruções expiram em até 30 minutos.</p>
            <Button variant="outline" onClick={() => navigate("/login")} className="w-full h-11 border-white/10">
              <ArrowLeft className="w-4 h-4 mr-2" /> Voltar ao Login
            </Button>
          </div>
        )}

        {step === "code" && (
          <>
            <div className="text-center">
              <h2 className="text-2xl font-bold">Código de Verificação</h2>
              <p className="text-muted-foreground mt-2">
                {email
                  ? <>Digite o código de 6 dígitos enviado para <strong>{email}</strong></>
                  : "Digite o código de 6 dígitos que está no email"}
              </p>
            </div>
            <form onSubmit={handleVerifyCode} className="space-y-5">
              <div className="space-y-2">
                <Label>Código</Label>
                <Input data-testid="reset-code-input" value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="000000" maxLength={6}
                  className="h-14 bg-black/20 border-white/10 text-center text-2xl font-mono tracking-[0.5em]" />
              </div>
              <Button data-testid="verify-code-btn" type="submit" disabled={loading || code.length !== 6}
                className="w-full h-11 bg-blue-600 hover:bg-blue-700 text-white font-semibold">
                {loading ? <div className="loading-spinner" /> : "Verificar Código"}
              </Button>
            </form>
            <div className="text-center">
              <button onClick={() => { setStep("email"); setCode(""); }} className="text-sm text-muted-foreground hover:text-foreground">
                <ArrowLeft className="w-3 h-3 inline mr-1" /> Tentar outro email
              </button>
            </div>
          </>
        )}

        {step === "new_password" && (
          <>
            <div className="text-center">
              <h2 className="text-2xl font-bold">Nova Senha</h2>
              <p className="text-muted-foreground mt-2">Escolha sua nova senha</p>
            </div>
            <form onSubmit={handleReset} className="space-y-5">
              <div className="space-y-2">
                <Label>Nova senha</Label>
                <div className="relative">
                  <Input data-testid="new-password-input" type={showPassword ? "text" : "password"}
                    value={password} onChange={(e) => setPassword(e.target.value)}
                    placeholder="Mínimo 6 caracteres" className="h-11 bg-black/20 border-white/10 pr-10" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Confirmar senha</Label>
                <Input data-testid="confirm-password-input" type="password"
                  value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Repita a senha" className="h-11 bg-black/20 border-white/10" />
                {password && confirmPassword && password !== confirmPassword && (
                  <p className="text-xs text-red-400">Senhas não coincidem</p>
                )}
              </div>
              <Button data-testid="reset-submit-btn" type="submit" disabled={loading}
                className="w-full h-11 bg-blue-600 hover:bg-blue-700 text-white font-semibold">
                {loading ? <div className="loading-spinner" /> : "Redefinir Senha"}
              </Button>
            </form>
          </>
        )}

        {step === "success" && (
          <div className="text-center space-y-4">
            <div className="w-16 h-16 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto">
              <Check className="w-8 h-8 text-emerald-400" />
            </div>
            <h2 className="text-2xl font-bold">Senha Redefinida!</h2>
            <p className="text-muted-foreground">Sua senha foi alterada com sucesso.</p>
            <Button onClick={() => navigate("/login")} className="w-full h-11 bg-blue-600 hover:bg-blue-700 text-white font-semibold">
              Fazer Login <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
        )}

        {step === "kyc_info" && (
          <div className="text-center space-y-4">
            <div className="w-16 h-16 rounded-full bg-yellow-500/10 flex items-center justify-center mx-auto">
              <Shield className="w-8 h-8 text-yellow-400" />
            </div>
            <h2 className="text-2xl font-bold">Sem Acesso ao Email?</h2>
            <p className="text-muted-foreground">Para sua segurança, será necessário passar por uma verificação de identidade (KYC) para atualizar seu email ou celular.</p>
            <div className="p-4 rounded-lg bg-yellow-500/5 border border-yellow-500/10 text-left space-y-2">
              <p className="text-sm"><strong>Como funciona:</strong></p>
              <p className="text-sm text-muted-foreground">1. Acesse a plataforma com seu login atual (se possível)</p>
              <p className="text-sm text-muted-foreground">2. No seu Perfil, inicie o processo de verificação KYC</p>
              <p className="text-sm text-muted-foreground">3. Após aprovação, seus dados de contato serão atualizados</p>
              <p className="text-sm text-muted-foreground">Se não conseguir acessar de nenhuma forma, entre em contato com o suporte da sua empresa.</p>
            </div>
            <Button variant="outline" onClick={() => setStep("email")} className="w-full h-11 border-white/10">
              <ArrowLeft className="w-4 h-4 mr-2" /> Voltar
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
